// Report It submission flow (v7 §6.1, MV-1077). Ties the client-snapshot
// capture to the API client: capture → assemble → submit. Dependency-injected
// (capture / submit / element accessors / entity provider) so the whole flow is
// unit-tested; the React hook that wires the live GameCast context is then a
// thin wrapper that supplies the real dependencies.

import {
  ReportItClientSnapshot,
} from './snapshotTypes';
import { CaptureReportItSnapshotOptions } from './captureSnapshot';
import {
  SubmitReportInput,
  SubmitReportResponse,
  ReportCorrelationEntities,
  ReportAttachmentFile,
  ReportIncidentType,
  ReportSymptomHint,
  ReportSeverityHint,
  collectBrowserReportContext,
  buildAttachmentDeclarations,
  uploadReportAttachments,
  UploadReportAttachmentsOptions,
} from './reportItApi';

/** Coarse phase of the submission, surfaced to the UI for status text. */
export type ReportSubmissionPhase = 'capturing' | 'submitting' | 'uploading';

/** The user- + session-supplied fields of a report. The flow fills in the
 *  captured snapshot, browser context, and correlation entities. */
export interface ReportDraft {
  environment: string;
  eventId?: string;
  /** Idempotency inputs (v7 §10.3) — generated once when the dialog opens. */
  browserSessionId: string;
  clientReportNonce: string;
  incidentType: ReportIncidentType;
  occurredAt: string;
  description?: string;
  symptomHints?: ReportSymptomHint[];
  severityHint?: ReportSeverityHint;
  attachDiagnostics?: boolean;
  attachments?: ReportAttachmentFile[];
}

export interface AssembleReportArgs {
  draft: ReportDraft;
  snapshot?: ReportItClientSnapshot;
  entities?: ReportCorrelationEntities;
  browserContext?: {
    userLocalTime: string;
    userTimeZone?: string;
    language?: string;
  };
}

/**
 * Merge a draft + captured snapshot + browser context + correlation entities
 * into the final SubmitReportInput (pure when `browserContext` is supplied;
 * otherwise reads browser globals via collectBrowserReportContext).
 */
export function assembleReportInput(args: AssembleReportArgs): SubmitReportInput {
  const ctx = args.browserContext ?? collectBrowserReportContext();

  return {
    environment: args.draft.environment,
    eventId: args.draft.eventId,
    browserSessionId: args.draft.browserSessionId,
    clientReportNonce: args.draft.clientReportNonce,
    incidentType: args.draft.incidentType,
    occurredAt: args.draft.occurredAt,
    description: args.draft.description,
    symptomHints: args.draft.symptomHints,
    severityHint: args.draft.severityHint,
    attachDiagnostics: args.draft.attachDiagnostics,
    attachments: buildAttachmentDeclarations(args.draft.attachments),
    userLocalTime: ctx.userLocalTime,
    userTimeZone: ctx.userTimeZone,
    language: ctx.language,
    entities: args.entities,
    clientSnapshot: args.snapshot,
  };
}

export interface ReportItSubmissionDeps {
  capture: (
    opts: CaptureReportItSnapshotOptions,
  ) => Promise<ReportItClientSnapshot>;
  submit: (input: SubmitReportInput) => Promise<SubmitReportResponse>;
  uploadAttachments?: (
    files: ReportAttachmentFile[] | undefined,
    uploads: SubmitReportResponse['attachmentUploads'],
    options?: UploadReportAttachmentsOptions,
  ) => Promise<void>;
  getVideoElement: () => HTMLVideoElement | null;
  getAudioElement?: () => HTMLMediaElement | null;
  getStats?: () => Promise<RTCStatsReport>;
  /** Latest stream connection state + transition history (MV-1077). */
  getConnectionState?: CaptureReportItSnapshotOptions['getConnectionState'];
  /** Page-lifetime rolling RTC window, preferred over on-demand sampling (MV-1077). */
  getBufferedRtcSamples?: CaptureReportItSnapshotOptions['getBufferedRtcSamples'];
  getEntities?: () => ReportCorrelationEntities;
  browserContext?: () => {
    userLocalTime: string;
    userTimeZone?: string;
    language?: string;
  };
  /** Notified as the flow moves through capture → submit → upload. */
  onPhaseChange?: (phase: ReportSubmissionPhase) => void;
  /** Per-file upload progress (0..1), keyed by declaration index. */
  onAttachmentProgress?: (index: number, fraction: number) => void;
  /** Notified when attachment upload fails *after* the report was created —
   *  the report itself still succeeded. */
  onAttachmentError?: (error: unknown) => void;
}

/**
 * Run the full submission flow: capture the client snapshot (best-effort —
 * a capture failure must not block the user's report), assemble the payload,
 * and submit. Returns the API response.
 */
export async function runReportItSubmission(
  deps: ReportItSubmissionDeps,
  draft: ReportDraft,
): Promise<SubmitReportResponse> {
  let snapshot: ReportItClientSnapshot | undefined;

  deps.onPhaseChange?.('capturing');

  try {
    snapshot = await deps.capture({
      video: deps.getVideoElement(),
      audio: deps.getAudioElement ? deps.getAudioElement() : undefined,
      getStats: deps.getStats,
      getConnectionState: deps.getConnectionState,
      getBufferedRtcSamples: deps.getBufferedRtcSamples,
    });
  } catch {
    // A diagnostic-capture failure must never stop a user from reporting — the
    // backend playbook degrades to needs_manual_review without a snapshot.
    snapshot = undefined;
  }

  const input = assembleReportInput({
    draft,
    snapshot,
    entities: deps.getEntities ? deps.getEntities() : undefined,
    browserContext: deps.browserContext ? deps.browserContext() : undefined,
  });

  deps.onPhaseChange?.('submitting');

  const response = await deps.submit(input);

  const upload = deps.uploadAttachments ?? uploadReportAttachments;
  const hasAttachments = !!draft.attachments && draft.attachments.length > 0;

  if (hasAttachments) {
    deps.onPhaseChange?.('uploading');
  }

  try {
    await upload(draft.attachments, response.attachmentUploads, {
      onProgress: deps.onAttachmentProgress,
    });
  } catch (err) {
    // The report is already created server-side, so a failed screenshot upload
    // must not present as a failed report — surface it as a warning and still
    // return the successful report response.
    deps.onAttachmentError?.(err);
  }

  return response;
}
